'use client';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { HtmlContent, Svg } from '@/components';
import { CloseIcon } from '@/icons';
import { twMerge } from 'tailwind-merge';
import { endpoints, http, logger, storage } from '@/config';
import Carousel from '@/layouts/Carousel';
import { usePathname } from 'next/navigation';
import { DEFAULT_STORE_CODE, REVALIDATE_TIME } from '@/constants';
import { isBrowser } from '@/utils/helper';
import { useHeaderContext } from '@/context/headerContext';
import { useLocation } from '@/context/locationContext';

const static_pages = [
  '/',
  '/gifting/',
  '/stories/',
  '/recipes/',
  '/about-us/',
  '/services/',
];

interface ClassNamesPropTypes {
  root?: string;
  closeIcon?: string;
  text?: string;
}

interface TickerPropTypes {
  classNames?: ClassNamesPropTypes;
}

interface TickerObject {
  entity_id: string;
  page_code: string;
  message: string;
  created_at: string;
}

async function getTickers(storeCode?: string) {
  try {
    const { data } = await http.get(endpoints.PAGE_TICKERS, {
      store_code: storeCode || DEFAULT_STORE_CODE,
    });
    return data;
  } catch (e) {
    logger.error('Error while fetching tickers:', e);
    return [];
  }
}

async function getCategoryAndProductTickersApi(
  pathname: string,
  storeCode?: string
) {
  const urlArr = pathname.split('/');
  const urlKey = urlArr[urlArr.length - 2];
  let giftingType = '';
  if (urlArr.includes('gifting')) {
    giftingType = 'gifting';
  }
  try {
    const { data } = await http.get(
      `page-notification?urlKey=${urlKey}&type=${giftingType}`,
      {
        next: { revalidate: REVALIDATE_TIME },
        store_code: storeCode || DEFAULT_STORE_CODE,
      }
    );
    return data;
  } catch (e) {
    logger.error('Error while fetching tickers:', e);
    return [];
  }
}

const Ticker: FC<TickerPropTypes> = ({ classNames }) => {
  const storeCode = storage.get('store_code') || undefined;
  const { isExpressDelivery, expressStandard } = useLocation();

  const pathname = usePathname();

  const { tickerClosed, closeTicker } = useHeaderContext();

  const [tickers, setTickers] = useState<TickerObject[]>([]);
  const [dynamicPageTickers, setDynamicPageTickers] = useState<string[]>([]);

  const isStaticPage = useMemo(() => {
    const pathArray = pathname.split('/');
    return (
      static_pages.includes(pathname) ||
      pathArray.includes('stories') ||
      pathArray.includes('recipes')
    );
  }, [pathname]);

  const staticPageTickers = useMemo(() => {
    if (!isStaticPage || !tickers || tickers.length === 0 || !pathname) {
      return [];
    }
    const currentPageTicker = tickers.find(
      ticker => ticker.page_code === pathname
    );
    if (
      currentPageTicker &&
      isBrowser &&
      !sessionStorage.getItem(`tickerClosed:${pathname}`)
    ) {
      return currentPageTicker.message.split(';');
    }
    return [];
  }, [isStaticPage, tickers, pathname]);

  const currentPageTickers = isStaticPage
    ? staticPageTickers
    : dynamicPageTickers;

  const fetchData = useCallback(async () => {
    const fetchedTickers = await getTickers(storeCode);
    if (!fetchedTickers || fetchedTickers?.length < 1) {
      setTickers([]);
    } else {
      setTickers(fetchedTickers);
    }
  }, [storeCode]);

  const closeTickerFunction = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (isBrowser) {
        sessionStorage.setItem(`tickerClosed:${pathname}`, 'true');
      }
      closeTicker(true);
    },
    [pathname, closeTicker]
  );

  const fetchCategoryAndProductTickers = useCallback(async () => {
    try {
      const response = await getCategoryAndProductTickersApi(
        pathname,
        storeCode
      );
      if (response?.page_notification) {
        const messagesArray = response.page_notification.split(';');
        if (messagesArray && messagesArray?.length > 0) {
          setDynamicPageTickers(messagesArray);
        } else {
          setDynamicPageTickers([]);
        }
      } else {
        setDynamicPageTickers([]);
      }
    } catch (error) {
      logger.error('Error while fetching category and product tickers:', error);
      setDynamicPageTickers([]);
    }
  }, [pathname, storeCode]);

  useEffect(() => {
    closeTicker(!currentPageTickers || currentPageTickers.length === 0);
  }, [currentPageTickers, closeTicker]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!isStaticPage) {
      fetchCategoryAndProductTickers();
    }
  }, [isStaticPage, fetchCategoryAndProductTickers]);

  if (!currentPageTickers || currentPageTickers.length === 0) {
    return null;
  }

  return (
    <div
      className={twMerge(
        'flex h-7.5 w-full items-center justify-center bg-[#D48A7A] py-[0.7rem]',
        tickerClosed && 'hidden',
        classNames?.root
      )}>
      <div className='w-full'>
        {currentPageTickers?.length > 1 ? (
          <Carousel
            settings={{
              centerMode: false,
              slidesToShow: 1,
              slidesToScroll: 1,
              responsive: [],
              progress: false,
              arrows: false,
              initialSlide: 0,
              draggable: false,
              swipe: false,
              dots: false,
              infinite: true,
              autoplay: true,
              speed: 500,
              autoplaySpeed: 5000,
            }}>
            {expressStandard && (
              <div className='text-center text-[0.75rem] text-black'>
                Get 2 hours Express Delivery in your area
              </div>
            )}
            {currentPageTickers.map((notification: any, index: number) => (
              <HtmlContent
                stripeStype={true}
                key={index}
                className='text-center text-[0.75rem] text-black'>
                {notification}
              </HtmlContent>
            ))}
            {isExpressDelivery && (
              <HtmlContent
                stripeStype={true}
                className='text-center text-[0.75rem] text-black'>
                45 mins express delivery available in your location
              </HtmlContent>
            )}
          </Carousel>
        ) : currentPageTickers.length === 1 ? (
          <HtmlContent
            stripeStype={true}
            className='text-center text-[0.75rem] text-black'>
            {currentPageTickers?.[0]}
          </HtmlContent>
        ) : null}
      </div>
      <button className='absolute right-2 top-1' onClick={closeTickerFunction}>
        <Svg
          className={twMerge(
            'stroke-black stroke-[1.5]',
            classNames?.closeIcon
          )}>
          <CloseIcon />
        </Svg>
      </button>
    </div>
  );
};

export default Ticker;
